import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import CreateOrganization from './components/CreateOrganization/CreateOrganization';
import NotFound from './components/NotFound/NotFound';
import VerifyEmail from './containers/Account/AccountSettings/ChangeEmail/VerifyEmail/VerifyEmail';
import Profile from './containers/Account/Profile/Profile';
import PublicProfile from './containers/Account/PublicProfile/PublicProfile';
import { ApplicationFlow } from './containers/ApplicationFlow/ApplicationFlow';
import { ApplicationFlowContainer } from './containers/ApplicationFlow/ApplicationFlowContainer';
import { ApplicationSuccess } from './containers/ApplicationFlow/ApplicationSuccess/ApplicationSuccess';
import { StrengthenApplication } from './containers/ApplicationFlow/StrengthenApplication/StrengthenApplication';
import CompanyPage from './containers/Company/CompanyPage/CompanyPage';
import Dashboard from './containers/Dashboard/Dashboard';
import { JobDetailPage } from './containers/JobDetailPage/JobDetailPage';
import { LeaderboardPage } from './containers/Leaderboard/LeaderboardPage';
import OrganizationSignup from './containers/OrganizationSignup/OrganizationSignup';
import PaymentsSettingsBase from './containers/Payments/PaymentsSettings/PaymentsBase';
import { RecommendationsPage } from './containers/Recommendations/RecommendationsPage';
import { RecommendedJobsPage } from './containers/RecommendedJobs/RecommendedJobsPage';
import { ReferralFlow } from './containers/ReferralFlow/ReferralFlow';
import { Strengthen } from './containers/ReferralFlow/Strengthen/Strengthen';
import { Success } from './containers/ReferralFlow/Success/Success';
import ConfirmationCode from './containers/SignUpSteps/ConfirmationCode/ConfirmationCode';
import ConfirmPhoneNumber from './containers/SignUpSteps/ConfirmPhoneNumber/ConfirmPhoneNumber';
import Location from './containers/SignUpSteps/Location/Location';
import RequestAccess from './containers/SignUpSteps/RequestAccess/RequestAccess';
import SlackRedirect from './containers/Slack/SlackRedirect';
import CandidateQuestions from './containers/StrengthenQuestions/CandidateQuestions/CandidateQuestions';
import Unsubscribe from './containers/Unsubscribe/Unsubscribe';
import { ReferralFlowProvider } from './contexts/ReferralFlowContext';
import usePageTracking from './hooks/usePageTracking';
import { getCurrentUser } from './redux/actions/authActions';
import { getCurrentStep } from './redux/actions/stepsAction';
import PrivateRoute from './routing/PrivateRoute/PrivateRoute';
import PublicRoute from './routing/PublicRoute/PublicRoute';
import AccountBase from './routing/RouteContainers/Account/AccountBase';
import BrowseBase from './routing/RouteContainers/Browse/BrowseBase';
import PaymentsBase from './routing/RouteContainers/Payments/PaymentsBase';
import TrackerNewBase from './routing/RouteContainers/TrackerNew/TrackerNewBase';
import urls from './routing/urls';
import { selectPhoneConfirmationData } from './selectors/authSelectors';

import './App.css';

const App = () => {
  usePageTracking();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { currentUser, loading } = useSelector((state) => state.authReducer);
  const { currentStep, loading: stepLoading } = useSelector((state) => state.stepsReducer);
  const phoneConfirmationData = useSelector(selectPhoneConfirmationData);

  useEffect(() => {
    if (!currentUser) {
      dispatch(getCurrentUser());
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (currentUser) {
      dispatch(getCurrentStep());
    }
  }, [currentUser, dispatch]);

  // Only force-redirect for real member-app onboarding routes. Steps like
  // create_password / confirm_email have no User-Web screens (AuthKit owns
  // them). The previous else → /dashboard yanked users off browse and
  // caused flicker whenever those legacy steps were still false.
  useEffect(() => {
    if (!currentUser || !currentStep || currentStep === 'home') {
      return;
    }

    if (currentStep === 'phone_number') {
      if (!phoneConfirmationData) {
        navigate(urls.confirmPhoneNumber);
      } else {
        navigate(urls.confirmationCode);
      }
    } else if (currentStep === 'location') {
      navigate(urls.location);
    } else if (currentStep === 'request_access') {
      navigate(urls.requestAccess);
    }
  }, [currentStep, navigate, currentUser, phoneConfirmationData]);

  useEffect(() => {
    if (localStorage.getItem('referralFlowData') && !pathname.includes('/refer')) {
      localStorage.removeItem('referralFlowData');
    }
  }, [pathname]);

  if (loading || stepLoading) {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const orgUuid = searchParams.get('orgUuid');

  return (
    <Routes>
      <Route exact path={urls.verifyAdditonalEmail} element={<VerifyEmail />} />
      <Route exact path={urls.confirmPhoneNumber} element={<ConfirmPhoneNumber />} />
      <Route exact path={urls.confirmationCode} element={<ConfirmationCode />} />
      <Route exact path={urls.location} element={<Location />} />
      <Route exact path={urls.requestAccess} element={<RequestAccess />} />
      <Route exact path="/" element={<PrivateRoute isDashboardBase />}>
        <Route path="/" element={<Navigate to={orgUuid ? `/dashboard?orgUuid=${orgUuid}` : '/dashboard'} replace />} />
        <Route path="/payments" element={<Navigate to="/payments/records" replace />} />
        {/* Nested route containers */}
        <Route path="/payments/*" element={<PaymentsBase />} />
        <Route path="/account/*" element={<AccountBase />} />
        <Route exact path="/jobs/company/:companyName" element={<CompanyPage />} />
        <Route path="/dashboard/*" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/recommendations" element={<RecommendationsPage />} />
        <Route path="/recommended-jobs" element={<RecommendedJobsPage />} />
        <Route path="/browse/*" element={<Navigate to="/jobs" />} />
        <Route path="/browse" element={<Navigate to="/jobs" />} />
        <Route path="/tracker/*" element={<TrackerNewBase />} />
        <Route path="/tracker" element={<TrackerNewBase />} />
        {/* Legacy /tracker-new URLs redirect to /tracker */}
        <Route path="/tracker-new/*" element={<Navigate to="/tracker" replace />} />
        <Route path="/tracker-new" element={<Navigate to="/tracker" replace />} />
        <Route path="/jobs/*" element={<BrowseBase />} />
        <Route path="/jobs" element={<BrowseBase />} />
        <Route path={`${urls.newPaymentMethod}/*`} element={<PaymentsSettingsBase />} />
        <Route path="/create-account/organization" element={<Navigate to="/createOrganization" replace />} />

        {/* Singular routes, make container if there are multiple with the same root path */}
        <Route exact path={urls.slackRedirect} element={<SlackRedirect />} />
      </Route>

      <Route exact path="/" element={<PrivateRoute />}>
        <Route exact path="/createOrganization" element={<CreateOrganization />} />
        <Route exact path={urls.publicProfile} element={<PublicProfile />} />

        <Route path="/" element={<ApplicationFlowContainer />}>
          <Route exact path={urls.applicationFlow} element={<ApplicationFlow />} />
          <Route exact path={urls.applicationFlowStrengthen} element={<StrengthenApplication />} />
          <Route exact path={urls.applicationFlowSuccess} element={<ApplicationSuccess />} />
        </Route>
      </Route>
      <Route exact path="/company" element={<PublicRoute isPublic />}>
        <Route exact path=":companyName" element={<CompanyPage isPublic />} />
        <Route path=":companyName/jobs/*" element={<BrowseBase isPublic />} />
      </Route>
      <Route exact path="/job" element={<PublicRoute isPublic />}>
        <Route exact path=":jobId" element={<JobDetailPage isPublic isJobPreview />} />
        <Route exact path="details/:jobId" element={<JobDetailPage isPublic />} />
      </Route>
      <Route exact path={urls.urlProfile} element={<Profile isPublic variant="url-profile" />} />
      <Route exact path={urls.candidateStrengthenQuestions} element={<CandidateQuestions />} />

      <Route
        exact
        path={urls.companyReferral}
        element={
          <ReferralFlowProvider>
            <ReferralFlow />
          </ReferralFlowProvider>
        }
      />
      <Route
        exact
        path={urls.companyReferralStrengthen}
        element={
          <ReferralFlowProvider>
            <Strengthen />
          </ReferralFlowProvider>
        }
      />
      <Route
        exact
        path={urls.companyReferralSuccess}
        element={
          <ReferralFlowProvider>
            <Success />
          </ReferralFlowProvider>
        }
      />

      <Route
        exact
        path={urls.refer}
        element={
          <ReferralFlowProvider>
            <ReferralFlow />
          </ReferralFlowProvider>
        }
      />
      <Route
        exact
        path={urls.referralFlowStrengthen}
        element={
          <ReferralFlowProvider>
            <Strengthen />
          </ReferralFlowProvider>
        }
      />
      <Route
        exact
        path={urls.referralFlowSuccess}
        element={
          <ReferralFlowProvider>
            <Success />
          </ReferralFlowProvider>
        }
      />

      <Route exact path={urls.companyApplicationFlow} element={<ApplicationFlow />} />
      <Route exact path={urls.companyApplicationFlowStrengthen} element={<StrengthenApplication />} />
      <Route exact path={urls.companyApplicationFlowSuccess} element={<ApplicationSuccess />} />

      <Route exact path={urls.organizationSignup} element={<OrganizationSignup />} />
      <Route path="*" element={<NotFound />} />
      <Route exact path={urls.unsubscribe} element={<Unsubscribe />} />
    </Routes>
  );
};

export default App;
